import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { parse } from "qs";
import { omit } from "lodash-es";
import { stringifyMinimal } from "../../utils/browser";
import { debugQueryParameterNames } from "../../types/debug";

const OVERLAY_QUERY_PARAMETER_NAME = "debugFeatures";

function localStorageKeyForDebugFeature(queryParameterName: string) {
  return `debug-${queryParameterName}`;
}

function isDebugFeatureEnabled({
  queryParameterName,
  parsedQs,
}: {
  queryParameterName: string;
  parsedQs: Record<string, unknown>;
}) {
  return (
    queryParameterName in parsedQs ||
    !!localStorage.getItem(localStorageKeyForDebugFeature(queryParameterName))
  );
}

export default function DebugFeaturesOverlay() {
  const location = useLocation();
  const history = useHistory();

  const parsedQs = useMemo(
    () => parse(location?.search ?? "", { ignoreQueryPrefix: true }),
    [location?.search]
  );

  const showOverlay = OVERLAY_QUERY_PARAMETER_NAME in parsedQs;
  if (!showOverlay) {
    return null;
  }

  const setSearch = (nextQs: Record<string, unknown>) => {
    history.replace({
      ...history.location,
      search: stringifyMinimal(nextQs, { addQueryPrefix: true }),
    });
  };

  const closeOverlay = () => {
    setSearch(omit(parsedQs, OVERLAY_QUERY_PARAMETER_NAME));
  };

  const toggleFeature = (queryParameterName: string, nextEnabled: boolean) => {
    const key = localStorageKeyForDebugFeature(queryParameterName);
    if (nextEnabled) {
      localStorage.setItem(key, "1");
      setSearch({
        ...parsedQs,
        [OVERLAY_QUERY_PARAMETER_NAME]: "1",
        [queryParameterName]: "1",
      });
    } else {
      localStorage.removeItem(key);
      setSearch(
        omit(
          {
            ...parsedQs,
            [OVERLAY_QUERY_PARAMETER_NAME]: "1",
          },
          queryParameterName
        )
      );
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000000,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 360,
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "calc(100vh - 24px)",
          overflow: "auto",
          borderRadius: 10,
          background: "rgba(20, 20, 20, 0.94)",
          color: "white",
          boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
          padding: 12,
          pointerEvents: "auto",
          fontSize: 13,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 700 }}>Debug features</div>
          <button
            type="button"
            onClick={closeOverlay}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "white",
              borderRadius: 8,
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        <div style={{ opacity: 0.9, marginBottom: 10 }}>
          Visible because <code>?{OVERLAY_QUERY_PARAMETER_NAME}</code> is set.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {debugQueryParameterNames.map((queryParameterName) => {
            const enabled = isDebugFeatureEnabled({
              queryParameterName,
              parsedQs,
            });

            return (
              <label
                key={queryParameterName}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: enabled
                    ? "rgba(76, 175, 80, 0.12)"
                    : "rgba(255,255,255,0.04)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) =>
                    toggleFeature(queryParameterName, e.target.checked)
                  }
                />
                <span style={{ fontFamily: "monospace" }}>
                  {queryParameterName}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
